if (!customElements.get('husar-hero-motion')) {
  customElements.define('husar-hero-motion', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.video = this.querySelector('video');
      this.image = this.querySelector('img');
      this.button = this.querySelector('button');
      this.slide = this.closest('.slideshow__slide');
      this.carousel = this.closest('slideshow-component');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 749px)');
      this.userPaused = false;
      this.manual = false;
      this.completed = false;
      this.cycles = 0;
      this.previousTime = 0;
      this.inView = false;
      this.active = this.slide.getAttribute('aria-hidden') !== 'true';
      this.button.hidden = false;
      this.button.addEventListener('click', () => {
        if (!this.video.paused && !this.video.ended) {
          this.userPaused = true;
        } else {
          this.manual = true;
          this.userPaused = false;
          this.completed = false;
          if (this.video.ended) this.video.currentTime = 0;
        }
        this.sync();
      }, options);
      this.video.addEventListener('play', () => {
        this.updateButton();
        const reveal = () => {
          if (this.isConnected && !this.video.paused) this.dataset.frame = 'ready';
        };
        if (this.video.requestVideoFrameCallback) this.video.requestVideoFrameCallback(reveal);
        else this.video.addEventListener('loadeddata', reveal, { ...options, once: true });
      }, options);
      this.video.addEventListener('pause', () => this.updateButton(), options);
      this.video.addEventListener('ended', () => {
        this.completed = true;
        this.updateButton();
      }, options);
      this.video.addEventListener('timeupdate', () => {
        if (this.previousTime > this.video.duration - 1 && this.video.currentTime < 1) this.cycles++;
        this.previousTime = this.video.currentTime;
      }, options);
      this.video.addEventListener('error', () => {
        this.failed = true;
        delete this.dataset.frame;
        this.button.hidden = true;
      }, options);
      this.observer = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        this.sync();
      }, { threshold: .25 });
      this.observer.observe(this);
      this.slideObserver = new MutationObserver(() => {
        const active = this.slide.getAttribute('aria-hidden') !== 'true';
        if (active && !this.active) {
          this.completed = false;
          this.cycles = 0;
          this.previousTime = 0;
          if (this.video.src) this.video.currentTime = 0;
          delete this.dataset.frame;
        }
        this.active = active;
        this.sync();
      });
      this.slideObserver.observe(this.slide, { attributes: true, attributeFilter: ['aria-hidden'] });
      document.addEventListener('visibilitychange', () => this.sync(), options);
      this.reduced.addEventListener('change', () => {
        this.manual = false;
        if (this.reduced.matches) delete this.dataset.frame;
        this.sync();
      }, options);
      this.image.decode().catch(() => {}).then(() => {
        if (!this.isConnected) return;
        this.posterReady = true;
        requestAnimationFrame(() => requestAnimationFrame(() => this.sync()));
      });
      customElements.whenDefined('slideshow-component').then(() => {
        if (!this.isConnected || !this.carousel) return;
        this.originalRotate = this.carousel.autoRotateSlides;
        this.rotationGuard = (...args) => {
          if (this.isConnected && this.active && this.inView && !this.video.paused && this.cycles === 0) return;
          return this.originalRotate.apply(this.carousel, args);
        };
        this.carousel.autoRotateSlides = this.rotationGuard;
        if (this.carousel.autoplayButtonIsSetToPlay) this.carousel.play();
      });
      this.updateButton();
    }
    sync() {
      const automaticAllowed = !this.reduced.matches && !navigator.connection?.saveData;
      if (!this.posterReady || !this.active || !this.inView || document.hidden || this.userPaused || this.failed || this.completed || (!automaticAllowed && !this.manual)) {
        this.video.pause();
        this.updateButton();
        return;
      }
      if (!this.video.getAttribute('src')) {
        const small = this.mobile.matches || this.getBoundingClientRect().width <= 749;
        this.video.src = small && this.dataset.mobileSrc ? this.dataset.mobileSrc : this.dataset.desktopSrc;
        this.video.muted = true;
      }
      if (this.pendingPlay || !this.video.paused) return;
      this.pendingPlay = true;
      this.video.play().catch(error => {
        if (error.name === 'AbortError') return;
        this.userPaused = true;
        delete this.dataset.frame;
        this.updateButton();
      }).finally(() => { this.pendingPlay = false; });
    }
    updateButton() {
      const paused = this.video.paused || this.video.ended;
      this.button.dataset.paused = String(paused);
      this.button.setAttribute('aria-label', paused ? this.button.dataset.play : this.button.dataset.pause);
    }
    disconnectedCallback() {
      this.video?.pause();
      this.controller?.abort();
      this.observer?.disconnect();
      this.slideObserver?.disconnect();
      if (this.carousel?.autoRotateSlides === this.rotationGuard) this.carousel.autoRotateSlides = this.originalRotate;
      this.controller = null;
    }
  });
}
